import {useEffect, useRef, useState} from "react";
import {Group, userOrGroup, WorkspaceMember} from "../../lib/types";
import {inputClasses} from "./Input";
import {Icon} from "./Icon";
import {useUserContext} from "../../providers/UserContext";
import {useModalContext} from "../../providers/ModalProvider";
import {MembersModal} from "../groups/MembersModal";
import {formatName} from "../../lib/utils/formatting";
import classNames from "classnames";

interface UserPickerProps {
  onSelect: (member: userOrGroup) => void;
  onRemove?: (member: userOrGroup) => void;
  selectedMembers?: userOrGroup[];
  inputClass?: string;
  allowGroups?: boolean;
  showInlineSelected?: boolean;
  label?: string;
}

export const UserPicker: React.FC<UserPickerProps> = ({ onSelect, selectedMembers = [], inputClass, allowGroups=false, showInlineSelected=false, onRemove, label="Add Members" }) => {
  const {activeWorkspace, groups, user} = useUserContext();
  const members = activeWorkspace?.members ? Object.values(activeWorkspace.members) : [];
  const [searchTerm, setSearchTerm] = useState("");
  const [focused, setFocused] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const allOptions: userOrGroup[] = [...(
    members.map((member): userOrGroup => ({
      type: "user",
      id: member.id,
      name: member.displayName,
      user: member,
    })
  ))];
  if (allowGroups) {
    allOptions.push(...(
      groups.filter(group => {
        return Object.keys(group.permissions).includes(user?.uid || "") || group.settings.whoCanPost === "everyone" || group.visibility === "public"
      }).map((group): userOrGroup => ({
        type: "group",
        id: group.id,
        name: group.name,
        group: group,
      })
    )));
  }
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredMembers: userOrGroup[] = searchTerm ? allOptions.filter(
    (member) =>
      (
        member.name.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
        member.user?.email.toLowerCase().startsWith(searchTerm.toLowerCase())
      ) &&
      !selectedMembers.some((selected) => selected.id === member.id)
  ).slice(0, 5) : []

  const selectMember = (member: userOrGroup) => {
    onSelect(member);
    setSearchTerm("");
    setFocused(true);
    if(focused && inputRef.current) {
      inputRef.current.focus();
    }
  }

  useEffect(() => {
    if(focused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focused]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {openModal} = useModalContext();
  return (
    <div className="w-full relative" ref={pickerRef}>
      {label && <span className="font-style-label-sm">{label}</span>}
      <div className="relative w-full inline-flex gap-2">
        <span className="absolute left-3 top-[14px] text-gray-3"><Icon name={"search"} size={"xsmall"}/></span>
        <div className={inputClasses+" pl-8 w-full flex items-center gap-4 flex-wrap !h-auto py-4 "+inputClass}>
          {
            showInlineSelected && selectedMembers.map((member) => (
              <div key={member.id} className={classNames(
                "inline-flex bg-gray-8 items-center gap-2 py-1 px-2 rounded-md font-style-body-sm text-gray-1",
                "select-none"
              )}>
                <button onClick={() => {
                  if(member.type === "group" && member.group ) {
                    openModal(<MembersModal group={member.group} />);
                  }
                }} className="inline-flex gap-2">
                  <div className="avatar text-white w-4 h-4 flex justify-center items-center rounded-full">
                    {member.type === "user" ? (<img src={member.user?.avatar} className={"w-full rounded-full"}
                                                    loading={"lazy"}/>) : member.group?.emoji}
                  </div>
                  <p>
                    {member.type === "group" ? member.name : formatName(member.name)}
                    {member.type === "group" && <span className="font-style-body-xxs text-gray-3"> (group)</span>}
                  </p>
                </button>
                <button onClick={(e) => {
                  e.preventDefault();
                  if (onRemove) {
                    onRemove(member);
                  }
                }}>
                  <Icon name={"close"} size={"xsmall"}/>
                </button>
              </div>
            ))
          }
          <input
            ref={inputRef}
            type="text"
            placeholder="Search members..."
            className="flex-grow inline-flex"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setFocused(true)}
          />
        </div>
      </div>
      {focused && filteredMembers.length > 0 && (
        <div
          className="absolute left-0 right-0 bg-white border border-gray-8 rounded-[0_0_4px_4px] -mt-1 shadow-elevation-1 z-10 max-h-60 overflow-y-auto">
          {filteredMembers.length > 0 && (
            filteredMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-2 hover:bg-gray-11 cursor-pointer"
                onClick={() => selectMember(member)}
              >
                <div className="flex items-center gap-2">
                  <div className="avatar bg-gray-500 text-white w-8 h-8 flex justify-center items-center rounded-full">
                    {member.type === "user" ? (<img src={member.user?.avatar } className={"w-full rounded-full"} loading={"lazy"}/>) : member.group?.emoji}
                  </div>
                  <div>
                    <p className="font-style-label-sm">{member.name}</p>
                    {member.type === "user" && (<p className="font-style-body-xs text-gray-2">{member.user?.email}</p>)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
